import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Pill, BadgeCheck, X as XIcon } from "lucide-react";

/**
 * Autocomplete inline sul prontuario farmaci (GET /medico/drugs).
 * Ricerca per nome commerciale E principio attivo. Selezione → onSelect(drug) con
 * la voce strutturata del prontuario; il testo libero resta possibile (legacy-safe).
 */
export default function DrugSearchInput({ value, linked, onText, onSelect, onUnlink, readOnly, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open || !(value || "").trim()) { setItems([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/medico/drugs", { params: { q: value.trim(), limit: 8 } });
        setItems(data.items || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [value, open]);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (linked) {
    return (
      <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 rounded-lg px-2 py-1 text-xs" data-testid={`${testid}-linked`}>
        <Pill size={11} className="text-emerald-600 shrink-0" />
        <span className="font-bold text-emerald-800">{linked.brand}</span>
        <span className="text-emerald-700 hidden sm:inline">({linked.principle})</span>
        {linked.verified && <BadgeCheck size={11} className="text-emerald-600" title="Verificato prontuario" />}
        {!readOnly && (
          <button type="button" onClick={onUnlink} data-testid={`${testid}-unlink`} title="Scollega dal prontuario" className="text-emerald-600 hover:text-red-600"><XIcon size={11} /></button>
        )}
      </span>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <input
        disabled={readOnly}
        value={value || ""}
        onChange={(e) => { onText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || "Cerca farmaco (nome o principio attivo)…"}
        data-testid={testid}
        className="px-2 py-1 rounded border border-[#E2E8F0] text-xs w-48 focus:outline-none focus:border-brand-azure"
      />
      {open && (value || "").trim() && (
        <div className="absolute z-40 mt-1 left-0 w-72 bg-white border border-[#E2E8F0] rounded-lg shadow-xl max-h-56 overflow-y-auto" data-testid={`${testid}-results`}>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Ricerca nel prontuario…</div>}
          {!loading && items.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Nessun farmaco trovato — resta come testo libero</div>}
          {items.map((d, i) => (
            <button key={`${d.atc}-${d.brand}-${i}`} type="button"
              data-testid={`${testid}-result-${i}`}
              onClick={() => { onSelect(d); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-brand-azure/10 border-b border-slate-50 last:border-0">
              <div className="text-xs font-bold text-brand-navy flex items-center gap-1.5">
                {d.brand} {d.verified && <BadgeCheck size={11} className="text-emerald-600" />}
              </div>
              <div className="text-[10px] text-slate-500">{d.principle} · {d.form} · {d.category}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
